import { type Mutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type {
  Account,
  AccountHoldingResponse,
  AccountWORiskAllocation,
  AccountWithMetrics,
} from "../../../../api/src/accounts/accounts.service";
import type {
  EditableHouseholdTrading,
  Household,
} from "../../../../api/src/households-base/households-base.service";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type {
  CurrentAndTargetRiskAllocation,
  HouseholdWithAccounts,
  HouseholdWithMetrics,
} from "../../../../api/src/households/households.service";
import type { SerializedObject, UnpackResponse } from "../../../../api/src/lib";
import type { RMDAnnual, RMDProfile } from "../../../../api/src/rmd/rmd-lib";
import type {
  EditableWorkflow,
  Workflow,
} from "../../../../api/src/tasks/tasks.service";
import {
  deserializeDate,
  useAuthenticatedMutationNew,
  useAuthenticatedQuery,
} from "../../lib/api";
import { processFileResponse } from "../../lib/file";
import { useAuthenticatedFetchAllPages } from "../../lib/pagination";
import {
  deserializeEvent,
  deserializeTask,
  deserializeWorkflow,
} from "../../tasks/lib";
import { deserializeAccountWithMetrics } from "../account/lib";
import { deserializeContact } from "../contact/lib";
import {
  deserializeAccountPercents,
  deserializeHoldingPercents,
  deserializeHouseholdAccountPercents,
  deserializePerformancePercents,
  deserializeRmdProfilePercents,
} from "../percents";

/**
 * The household the household and rebalance pages hold. It is what
 * `GET /households/:id?accounts=true` sends (`HouseholdWithAccounts`) with a
 * `riskAllocation` laid over the household and each account by the page:
 * `HouseholdInfo.tsx` from `GET /households/:id/risk-allocation`,
 * `RebalanceInfo.tsx` from the rebalance. That route sends no `riskAllocation`,
 * so it is absent until the page has the second response.
 */
export type HouseholdWithAccountsAndRiskAllocation = Omit<
  HouseholdWithAccounts,
  "accounts"
> & {
  riskAllocation?: CurrentAndTargetRiskAllocation;
  accounts: (AccountWORiskAllocation & {
    riskAllocation?: CurrentAndTargetRiskAllocation;
  })[];
};

/**
 * `GET /households` sends `HouseholdWithMetrics` with `balances=true` and
 * `Household` without it.
 */
type HouseholdsWithBalances<TBalances extends boolean> = TBalances extends true
  ? HouseholdWithMetrics
  : Household;

/**
 * The path of `getHouseholds`. Also the FIRST element of every react-query key
 * the page walk in `useQueryHouseholds` uses, so `[HOUSEHOLDS_PATH]` reaches
 * every page of every walk, whatever its filters.
 */
export const HOUSEHOLDS_PATH = "/v1/households";

/** One item of `GET /households`, as the route serialises it. */
type SerializedHouseholdListItem = SerializedObject<
  UnpackResponse<HouseholdsController["getHouseholds"]>
>["data"][number];

/**
 * True for a create, update or delete under `/households`: every mutation key
 * whose method is `POST`, `PUT` or `DELETE` and whose first path element is
 * `"households"` (`useUpdateHouseholdModelTrading`, `useArchiveHousehold`,
 * `useUnarchiveHousehold`, the model assignment of `Households.tsx`, among
 * others).
 */
const isHouseholdWrite = (mutation: Mutation<unknown, unknown, unknown>) => {
  const [method, root] = mutation.options.mutationKey ?? [];
  return (
    ["POST", "PUT", "DELETE"].includes(String(method)) && root === "households"
  );
};

/**
 * Drops every cached page of the household list, so the next walk reads it
 * again rather than being handed a page from before a write. Invalidating is
 * not enough: a finished walk holds no observer on its first page, so a
 * restarted walk would be handed the cached page and stop there
 * (`removeModelListPages`, `models/lib.tsx`, for the same reason).
 */
function removeHouseholdListPages(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.removeQueries({ queryKey: [HOUSEHOLDS_PATH] });
}

/**
 * Every household the user can see. `GET /households` answers a page
 * (APP-1653), and every screen that reads this hook shows, searches or looks
 * up the whole list (the Households screen, the billing, cash, rebalance and
 * report screens, the insights), so this walks every page
 * (`useAuthenticatedFetchAllPages`) and holds `data` back until the last one
 * is in. Each record goes through `deserializeHouseholdWithMetrics`, as before.
 *
 * When a write under `/households` succeeds, the cached pages are dropped and
 * the walk starts again, so a mounted list shows the write. When the last
 * reader of the list unmounts, the cached pages are dropped too: a reader
 * mounted later walks the list again rather than collecting pages cached
 * before a write this hook did not see (most writes under `/households` are
 * sent through `useAuthenticatedMutationAsync`, which no mutation cache
 * records). Before APP-1653 a remount refetched the list for the same reason.
 *
 * `enabled: false` asks for nothing (`useAuthenticatedFetchAllPages`); `data`
 * stays `undefined` and `isLoading` `false` until it is `true`, as with
 * react-query's own `enabled`.
 */
export function useQueryHouseholds<TBalances extends boolean = false>({
  includeBalances = false as TBalances,
  includeInactive = false,
  includeCRM = false,
  enabled = true,
}: {
  includeBalances?: TBalances;
  includeInactive?: boolean;
  includeCRM?: boolean;
  enabled?: boolean;
} = {}) {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedHouseholdListItem>(
      HOUSEHOLDS_PATH,
      {
        balances: String(includeBalances),
        "include-inactive": String(includeInactive),
        crm: String(includeCRM),
      },
      { enabled },
    );

  // Restart the walk when a household write succeeds. Subscribed to the
  // mutation cache's events rather than read from `useIsMutating`: a write that
  // starts and settles within one render batch never shows a pending count.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isHouseholdWrite(event.mutation)
        ) {
          removeHouseholdListPages(queryClient);
          reload();
        }
      }),
    [queryClient, reload],
  );

  // On unmount, drop the pages no other mounted reader still observes. This
  // cleanup runs after the walk's own observer has unsubscribed, because the
  // walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [HOUSEHOLDS_PATH],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient],
  );

  const data: { data: HouseholdsWithBalances<TBalances>[] } | undefined =
    useMemo(
      () =>
        hasEveryRow
          ? {
              data: rows.map(
                (household) =>
                  deserializeHouseholdWithMetrics(
                    household,
                  ) as HouseholdsWithBalances<TBalances>,
              ),
            }
          : undefined,
      [rows, hasEveryRow],
    );

  const isPending = !hasEveryRow && !isError;

  return {
    isPending,
    isLoading: isPending && enabled,
    isError,
    data,
  };
}

/**
 * `GET /households/:id` sends `HouseholdWithAccounts` with `accounts=true` and
 * `Household` without it.
 */
type HouseholdWithOptionalAccounts<TAccounts extends boolean> =
  TAccounts extends true ? HouseholdWithAccounts : Household;

export function useQueryHousehold<TAccounts extends boolean = false>(
  id: number,
  {
    includeAccounts = false as TAccounts,
    includeCRM = false,
    enabled = true,
  }: {
    includeAccounts?: TAccounts;
    includeCRM?: boolean;
    enabled?: boolean;
  } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<HouseholdsController["getHousehold"]>,
    HouseholdWithOptionalAccounts<TAccounts>,
    [
      string,
      number,
      {
        includeAccounts: boolean;
        includeCRM: boolean;
      },
    ]
  >({
    queryKey: ["households", id, { includeAccounts, includeCRM }],
    queryFn: async (household) =>
      deserializeHouseholdWithMetrics(
        // With `accounts=true` each account's percents come on 0 to 100.
        deserializeHouseholdAccountPercents(household.data),
      ) as HouseholdWithOptionalAccounts<TAccounts>,
    urlBuilderFn: (key) =>
      `/v1/${key[0]}/${key[1]}/?accounts=${key[2].includeAccounts}&crm=${key[2].includeCRM}`,
    queryOptions: { enabled },
  });
}

/**
 * `GET /households/:id/accounts` sends `AccountWithMetrics` with
 * `balances=true` and `Account` without it.
 */
type HouseholdAccountsWithBalances<TBalances extends boolean> =
  TBalances extends true ? AccountWithMetrics : Account;

/**
 * The path of `getHouseholdAccounts` for one household. Also the FIRST element
 * of every react-query key the page walk in `useQueryHouseholdAccounts` uses,
 * so `[householdAccountsPath(id)]` reaches every page of that walk.
 */
export const householdAccountsPath = (id: number) =>
  `/v1/households/${id}/accounts`;

/** One item of `GET /households/:id/accounts`, as the route serialises it. */
type SerializedHouseholdAccount = SerializedObject<
  UnpackResponse<HouseholdsController["getHouseholdAccounts"]>
>["data"][number];

/**
 * True for a create, update or delete under `/accounts`: every mutation key
 * whose method is `POST`, `PUT` or `DELETE` and whose first path element is
 * `"accounts"` (`AccountTaxableField.tsx`, `AccountTaxRegistrationField.tsx`,
 * the archive and model assignment writes of `clients/account/lib.ts`, among
 * others).
 */
const isAccountWrite = (mutation: Mutation<unknown, unknown, unknown>) => {
  const [method, root] = mutation.options.mutationKey ?? [];
  return (
    ["POST", "PUT", "DELETE"].includes(String(method)) && root === "accounts"
  );
};

/**
 * Every account of the household. `GET /households/:id/accounts` answers a
 * page in `account_id` order (APP-1653), and its reader
 * (`AccountBillingInfo.tsx`, which lists the household's other accounts as
 * fee locations) needs every account, so this walks every page
 * (`useAuthenticatedFetchAllPages`) and holds `data` back until the last one
 * is in. Each account goes through `deserializeAccountPercents` and
 * `deserializeAccountWithMetrics`, as before.
 *
 * As `useQueryHouseholds` does: when a write under `/households` or
 * `/accounts` succeeds, the cached pages are dropped and the walk starts
 * again, and when the last reader unmounts, the cached pages are dropped, so
 * a reader mounted later walks again rather than collecting pages cached
 * before a write the mutation cache never saw.
 *
 * When `id` changes while the reader stays mounted (`AccountBillingInfo.tsx`
 * reads the household off the account in the url), the walk starts again for
 * the new household and `data` stays `undefined` until it is in, so no account
 * of one household is shown as another's.
 *
 * `enabled: false` asks for nothing; `data` stays `undefined` and `isLoading`
 * and `isFetching` `false`.
 */
export function useQueryHouseholdAccounts<TBalances extends boolean = false>(
  id: number,
  {
    includeBalances = false as TBalances,
    enabled = true,
  }: { includeBalances?: TBalances; enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const path = householdAccountsPath(id);

  // The household whose accounts the walk below holds. It trails `id` by one
  // render when `id` changes; the walk is held and `data` withheld meanwhile.
  const [walkedId, setWalkedId] = useState(id);
  const isWalkOfId = walkedId === id;

  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedHouseholdAccount>(
      path,
      { balances: String(includeBalances) },
      { enabled: enabled && isWalkOfId },
    );

  useEffect(() => {
    if (!isWalkOfId) {
      reload();
      setWalkedId(id);
    }
  }, [id, isWalkOfId, reload]);

  // Restart the walk when a household or account write succeeds. Subscribed to
  // the mutation cache's events, as in `useQueryHouseholds`.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          (isHouseholdWrite(event.mutation) || isAccountWrite(event.mutation))
        ) {
          queryClient.removeQueries({ queryKey: [path] });
          reload();
        }
      }),
    [queryClient, path, reload],
  );

  // On unmount, or when the household changes, drop the pages of the path no
  // other mounted reader still observes. This cleanup runs after the walk's
  // own observer has unsubscribed, because the walk's hooks are called above
  // it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [path],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient, path],
  );

  const data: HouseholdAccountsWithBalances<TBalances>[] | undefined = useMemo(
    () =>
      hasEveryRow && isWalkOfId
        ? rows.map(
            (account) =>
              deserializeAccountWithMetrics(
                // With `balances=true` the percents come on 0 to 100.
                deserializeAccountPercents(account),
              ) as HouseholdAccountsWithBalances<TBalances>,
          )
        : undefined,
    [rows, hasEveryRow, isWalkOfId],
  );

  const isPending = data === undefined && !isError;
  const isLoading = isPending && enabled;

  return {
    isPending,
    isLoading,
    // Every page is read before `data` is set, so the walk is fetching for as
    // long as it is loading.
    isFetching: isLoading,
    isError,
    data,
  };
}

/**
 * The path of `getHouseholdPerformance` for one household. Also the FIRST
 * element of every react-query key the page walk in
 * `useQueryHouseholdPerformance` uses, so `[householdPerformancePath(id)]`
 * reaches every page of that walk.
 */
export const householdPerformancePath = (id: number) =>
  `/v1/households/${id}/performance`;

/** One item of `GET /households/:id/performance`, as the route serialises it. */
type SerializedHouseholdPerformance = SerializedObject<
  UnpackResponse<HouseholdsController["getHouseholdPerformance"]>
>["data"][number];

/**
 * One `AccountPerformance` per account of the household, over the period
 * `periodParams` names (the `start-date` and `end-date` query string the
 * period selectors build, or `""`). `GET /households/:id/performance`
 * answers a page in `accountId` order (APP-1653), and its readers
 * (`HouseholdPerformance.tsx`, `HouseholdTax.tsx`, `HouseholdTaxSummary.tsx`)
 * sum over every account, so this walks every page
 * (`useAuthenticatedFetchAllPages`) and holds `data` back until the last one
 * is in. Each entry's `performance` goes through
 * `deserializePerformancePercents`, as before.
 *
 * As `useQueryHouseholdAccounts` does: when a write under `/households` or
 * `/accounts` succeeds, the cached pages are dropped and the walk starts
 * again; when the last reader unmounts, the cached pages are dropped; and
 * when `id` or `periodParams` changes while the reader stays mounted, the
 * walk starts again and `data` stays `undefined` until it is in.
 *
 * An `id` of `undefined` (the household not yet loaded) or `enabled: false`
 * asks for nothing.
 */
export function useQueryHouseholdPerformance(
  id: number | undefined,
  periodParams: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const path = householdPerformancePath(id ?? 0);
  const query = useMemo(
    () => Object.fromEntries(new URLSearchParams(periodParams)),
    [periodParams],
  );

  // The household and period whose performance the walk below holds. It
  // trails them by one render when either changes; the walk is held and
  // `data` withheld meanwhile.
  const walkKey = `${id}?${periodParams}`;
  const [walkedKey, setWalkedKey] = useState(walkKey);
  const isWalkOfKey = walkedKey === walkKey;

  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedHouseholdPerformance>(path, query, {
      enabled: enabled && id !== undefined && isWalkOfKey,
    });

  useEffect(() => {
    if (!isWalkOfKey) {
      reload();
      setWalkedKey(walkKey);
    }
  }, [walkKey, isWalkOfKey, reload]);

  // Restart the walk when a household or account write succeeds. Subscribed to
  // the mutation cache's events, as in `useQueryHouseholdAccounts`.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          (isHouseholdWrite(event.mutation) || isAccountWrite(event.mutation))
        ) {
          queryClient.removeQueries({ queryKey: [path] });
          reload();
        }
      }),
    [queryClient, path, reload],
  );

  // On unmount, or when the household changes, drop the pages of the path no
  // other mounted reader still observes. This cleanup runs after the walk's
  // own observer has unsubscribed, because the walk's hooks are called above
  // it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [path],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient, path],
  );

  const data = useMemo(
    () =>
      hasEveryRow && isWalkOfKey
        ? rows.map(deserializePerformancePercents)
        : undefined,
    [rows, hasEveryRow, isWalkOfKey],
  );

  return { isPending: data === undefined && !isError, isError, data };
}

/**
 * The path of `RMDController.getProfiles` for one household, under `/v1`
 * (APP-1653). Also the FIRST element of every react-query key the page walk in
 * `useQueryRmdProfiles` uses, so `[rmdProfilesPath(householdId)]` reaches
 * every page of that walk; every `invalidateQueries` of those keys builds them
 * with this function (`HouseholdRMD.tsx`).
 */
export const rmdProfilesPath = (householdId: number | string) =>
  `/v1/households/${householdId}/rmd/profiles`;

/**
 * The path of `RMDController.getAnnuals` for one profile, under `/v1`
 * (APP-1653). Also the FIRST
 * element of every react-query key the page walk in `useQueryRmdAnnuals`
 * uses, so `[rmdAnnualsPath(householdId, profileId)]` reaches every page of
 * that walk.
 */
export const rmdAnnualsPath = (
  householdId: number | string,
  profileId: number,
) => `${rmdProfilesPath(householdId)}/${profileId}/annuals`;

/**
 * Every row of the RMD list at `path`, walked a page at a time
 * (`useAuthenticatedFetchAllPages`), for `useQueryRmdProfiles` and
 * `useQueryRmdAnnuals`. `rows` stays `undefined` until the last page is in.
 *
 * When `path` changes while the reader stays mounted, the walk starts again
 * and `rows` is `undefined` until it is in. When the last reader unmounts,
 * the cached pages are dropped.
 *
 * The walk starts again when a query under `[path]` is invalidated. Every
 * console write to RMD profiles, annuals or the ledger is followed by an
 * `invalidateQueries` that reaches these keys (`handleCreateProfile` and
 * `invalidate` in `HouseholdRMD.tsx`, `invalidateLedger` in `RMDLedger.tsx`),
 * and one of them, the ledger entry update in `RMDLedger.tsx`, is sent through
 * `authenticatedFetch`, which no mutation cache records. Invalidating alone
 * refetches nothing here: a finished walk observes no query it has fetched
 * (`useAuthenticatedFetchAllPages`), so the pages are dropped and the walk
 * restarted. While that walk runs, `rows` keeps the rows of the walk before
 * it, as a refetch after `invalidateQueries` kept the response before
 * APP-1653: `RMDAccountDetail` in `HouseholdRMD.tsx` shows "Loading profile…"
 * in place of the detail while it has no profiles, which would drop the
 * detail's own state (its tab, its open modal) on every write.
 */
function useRmdListPages<TRow>(path: string, enabled: boolean) {
  const queryClient = useQueryClient();

  // The path whose rows the walk below holds. It trails `path` by one render
  // when it changes; the walk is held and `rows` withheld meanwhile.
  const [walkedPath, setWalkedPath] = useState(path);
  const isWalkOfPath = walkedPath === path;

  const {
    rows: walkedRows,
    hasEveryRow,
    isError,
    reload,
  } = useAuthenticatedFetchAllPages<TRow>(
    path,
    {},
    { enabled: enabled && isWalkOfPath },
  );

  useEffect(() => {
    if (!isWalkOfPath) {
      reload();
      setWalkedPath(path);
    }
  }, [path, isWalkOfPath, reload]);

  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "invalidate" &&
          event.query.queryKey[0] === path
        ) {
          queryClient.removeQueries({ queryKey: [path] });
          reload();
        }
      }),
    [queryClient, path, reload],
  );

  // On unmount, or when the path changes, drop the pages of the path no other
  // mounted reader still observes. This cleanup runs after the walk's own
  // observer has unsubscribed, because the walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [path],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient, path],
  );

  const completeRows = hasEveryRow && isWalkOfPath ? walkedRows : undefined;

  // The rows of the last walk of this path that reached its last page.
  const [lastComplete, setLastComplete] = useState<{
    path: string;
    rows: TRow[];
  }>();
  useEffect(() => {
    if (completeRows !== undefined) {
      setLastComplete({ path, rows: completeRows });
    }
  }, [path, completeRows]);

  return {
    rows:
      completeRows ??
      (lastComplete?.path === path ? lastComplete.rows : undefined),
    isError,
  };
}

/**
 * Every RMD profile of the household, each with its `annuals`.
 * `GET /households/:householdId/rmd/profiles` answers a page in
 * `created_time` order (APP-1653), and its readers (`HouseholdRMD.tsx`,
 * `AccountTax.tsx`) list or search every profile, so this walks every page
 * and holds `data` back until the last one is in (`useRmdListPages`). Each
 * profile goes through `deserializeRmdProfilePercents`, as before; its dates
 * stay as sent, as before.
 *
 * `enabled: false` asks for nothing.
 */
export function useQueryRmdProfiles(
  householdId: number | string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const { rows, isError } = useRmdListPages<RMDProfile>(
    rmdProfilesPath(householdId),
    enabled,
  );
  const data = useMemo(() => rows?.map(deserializeRmdProfilePercents), [rows]);

  return { isPending: data === undefined && !isError, isError, data };
}

/**
 * Every annual record of one RMD profile, each with its `distributions`.
 * `GET /households/:householdId/rmd/profiles/:profileId/annuals` answers a
 * page in `year` order (APP-1653), and its readers (`HouseholdRMD.tsx`,
 * `AccountTax.tsx`) look up the current year's record among them, so this
 * walks every page and holds `data` back until the last one is in
 * (`useRmdListPages`). The records stay as sent, as before.
 *
 * `enabled: false` asks for nothing.
 */
export function useQueryRmdAnnuals(
  householdId: number | string,
  profileId: number,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const { rows: data, isError } = useRmdListPages<RMDAnnual>(
    rmdAnnualsPath(householdId, profileId),
    enabled,
  );

  return { isPending: data === undefined && !isError, isError, data };
}

export function useQueryHouseholdHoldings(
  id: number,
  { customTaxPeriodParams = "", enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<HouseholdsController["getHouseholdHoldings"]>,
    {
      id: number;
      holdings: AccountHoldingResponse[];
    }[]
  >({
    queryKey: ["households", id, "holdings", customTaxPeriodParams],
    // Each holding's `security.riskAllocation` comes on 0 to 100. A held
    // security's `asOfDate` and `firstOpenDate` come as strings, now that
    // `AccountHoldingResponse` declares them (APP-1653), and are parsed as
    // `deserializeAccountWithMetrics` (`../account/lib`) parses a holding's.
    queryFn: async (accounts) =>
      accounts.data.accounts.map((account) => ({
        ...account,
        holdings: account.holdings.map((holding) => ({
          ...deserializeHoldingPercents(holding),
          asOfDate: deserializeDate(holding.asOfDate),
          firstOpenDate: deserializeDate(holding.firstOpenDate),
        })),
      })),
    urlBuilderFn: (key) => `/v1/${key[0]}/${key[1]}/${key[2]}/?${key[3]}`,
    queryOptions: { enabled },
  });
}

export function useUpdateHouseholdModelTrading(householdId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdsController["updateHouseholdTradingSettings"]>,
    Household,
    EditableHouseholdTrading
  >({
    // The key keeps `"households"` first, so `isHouseholdWrite` matches it;
    // the url is under `/v1` (APP-1653).
    mutationKey: ["PUT", "households", householdId, "trading"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({ data }) => {
      queryClient.invalidateQueries({
        queryKey: ["households", householdId, { includeCRM: true }],
      });
      queryClient.invalidateQueries({
        queryKey: [`/v1/households/${householdId}/allocation-details`],
      });

      return deserializeHouseholdWithMetrics(data);
    },
  });
}

export function useArchiveHousehold(householdId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdsController["archiveHousehold"]>,
    void,
    void
  >({
    // The key keeps `"households"` first, so `isHouseholdWrite` matches it;
    // the url is under `/v1` (APP-1653). The response is the household read
    // back, which this hook does not read: it invalidates the read instead.
    mutationKey: ["POST", "households", householdId, "archive-date"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "households",
          householdId,
          { includeCRM: true, includeAccounts: true },
        ],
      });
    },
  });
}

export function useUnarchiveHousehold(householdId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdsController["unarchiveHousehold"]>,
    void,
    void
  >({
    mutationKey: ["DELETE", "households", householdId, "archive-date"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "households",
          householdId,
          { includeCRM: true, includeAccounts: true },
        ],
      });
    },
  });
}

export function useCreateHouseholdWorkflow(householdId: number) {
  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdsController["createWorkflowForHousehold"]>,
    Workflow | null,
    EditableWorkflow
  >({
    mutationKey: ["POST", "households", householdId, "workflows"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({ data }) =>
      data === null ? null : deserializeWorkflow(data),
  });
}

export function displayHouseholdStatus(household: Pick<Household, "isActive">) {
  return household.isActive ? "Active" : "Inactive";
}

/**
 * Deserializes any household a route sends. `accounts` and `lastRebalanceDate`
 * are not keys of `Household`: only `HouseholdWithAccounts`
 * (`GET /households/:id?accounts=true`) carries them, so each is read only when
 * present, as it always has been.
 */
export function deserializeHouseholdWithMetrics<THousehold extends Household>(
  household: SerializedObject<THousehold>,
): THousehold {
  const { accounts, lastRebalanceDate } = household as Partial<
    SerializedObject<
      Pick<HouseholdWithAccounts, "accounts" | "lastRebalanceDate">
    >
  >;

  return {
    ...household,
    accounts: accounts?.map(deserializeAccountWithMetrics),
    rebalancePeriodStartDate: deserializeDate(
      household.rebalancePeriodStartDate,
    ),
    lastRebalanceDate: deserializeDate(lastRebalanceDate),
    latestRebalanceDate: deserializeDate(household.latestRebalanceDate),
    memberDates: household.memberDates.map((member) => ({
      ...member,
      anniversary: deserializeDate(member.anniversary),
      birthDate: deserializeDate(member.birthDate),
      clientSinceDate: deserializeDate(member.clientSinceDate),
      retirementDate: deserializeDate(member.retirementDate),
    })),
    members: household.members.map(deserializeContact),
    events: household.events.map(deserializeEvent),
    tasks: household.tasks.map(deserializeTask),
    workflows: household.workflows.map(deserializeWorkflow),
    archiveDate: deserializeDate(household.archiveDate),
  } as THousehold;
}

export function useQueryExportHouseholds() {
  return useAuthenticatedQuery<
    UnpackResponse<HouseholdsController["exportHouseholds"]>,
    void,
    [string, string]
  >({
    queryKey: ["households", "export"],
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
}

export function useQueryExportHouseholdRebalance(householdId: number) {
  return useAuthenticatedQuery<
    UnpackResponse<HouseholdsController["exportForRebalance"]>,
    void,
    [string, number, string]
  >({
    queryKey: ["households", householdId, "export-rebalance"],
    queryFn: async () => undefined,
    urlBuilderFn: (key) => `/${key[0]}/${key[1]}/${key[2]}`,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
}
